/**
 * NOTIFICATION SYNC — LEADER ELECTION + BROADCASTCHANNEL
 *
 * Problem, który rozwiązujemy:
 *   Każda karta przeglądarki ma własny QueryClient i własny timer refetchInterval.
 *   100 userów × 10 kart = 1000 requestów co 30 sekund tylko dla powiadomień.
 *
 * Rozwiązanie:
 *   Karty w tej samej przeglądarce koordynują się przez BroadcastChannel
 *   (komunikacja wewnątrz przeglądarki, zero ruchu sieciowego) i localStorage
 *   (służy jako współdzielona blokada do wyboru lidera).
 *
 *   Tylko jedna karta — "lider" — wysyła requesty do backendu.
 *   Po otrzymaniu danych lider broadcastuje je do pozostałych kart,
 *   które aktualizują swój cache TanStack Query bez żadnego HTTP.
 *
 * Efekt:
 *   100 userów × 10 kart → nadal tylko 100 requestów/30s (jeden na przeglądarkę).
 *
 * Co lider pobiera (od 2026-10-20): TYLKO liczniki (unread_count, z liczbą
 *   nieprzeczytanych wycen). Wcześniej co 30 s szły też pełna lista powiadomień i CAŁA
 *   lista wycen — ~33 tys. requestów dziennie, z czego zamknięty dzwonek potrzebował
 *   tylko liczb. Listy pobiera sam dzwonek, gdy jest otwarty; gdy licznik się
 *   zmieni, lider unieważnia listy — przeładuje je tylko otwarty dzwonek.
 *
 * Fallback:
 *   Jeśli BroadcastChannel nie jest dostępny (stary Safari, niektóre tryby prywatne),
 *   każda karta wraca do samodzielnego pollingu — identycznie jak przed zmianą.
 *
 * Schemat wyboru lidera:
 *   - Karta zapisuje timestamp w localStorage["elg5-notif-leader-ts"] jako "heartbeat"
 *   - Nowa karta sprawdza: czy heartbeat jest świeży (< 35s temu)?
 *     - TAK → jest żywy lider, słuchaj broadcastów
 *     - NIE → ogłoś się liderem, zacznij pollować
 *   - Co LEADER_TTL_MS każda karta sprawdza czy lider żyje (watchdog)
 *   - Przy zamknięciu karty lider usuwa swój heartbeat (beforeunload)
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  notificationsApi,
  type NotificationListResult,
  type UnreadCount,
} from "@/features/tasks/api/notifications";
import type { TaskNotification } from "@/features/tasks/types";
import type { MyPricingNotification } from "@/features/tenders/types";

// ── Stałe konfiguracyjne ──────────────────────────────────────────────────────

/** Nazwa kanału — musi być identyczna we wszystkich kartach tej samej domeny. */
const CHANNEL_NAME = "elg5-notif-sync";

/** Klucz w localStorage przechowujący timestamp ostatniego heartbeatu lidera. */
const LEADER_STORAGE_KEY = "elg5-notif-leader-ts";

/**
 * Jak stary może być heartbeat, żeby nadal uznać lidera za żywego.
 * Celowo nieco większy niż HEARTBEAT_MS, żeby nie było false-positive przy
 * chwilowym lagowaniu przeglądarki.
 */
const LEADER_TTL_MS = 35_000;

/** Jak często lider odpytuje backend. */
const POLL_MS = 30_000;

/** Po ilu ms bezczynności wstrzymujemy polling. */
const IDLE_THRESHOLD_MS = 5 * 60_000;

/**
 * Jak często lider odświeża swój heartbeat w localStorage.
 * Musi być < LEADER_TTL_MS, żeby inne karty nie uznały go za martwego.
 */
const HEARTBEAT_MS = 10_000;

// ── Typy wiadomości BroadcastChannel ─────────────────────────────────────────

/**
 * Kształt wiadomości przesyłanych między kartami.
 * Discriminated union — każdy `type` ma swój zestaw pól.
 */
type SyncMessage =
  | {
      /** Lider wysyła świeże liczniki. */
      type: "data";
      unreadCount: UnreadCount;
      // Listy wysyła już tylko karta z poprzedniej wersji aplikacji (do jej
      // przeładowania) — odbiór je przyjmuje, więc wdrożenie nie psuje otwartych kart.
      // Odwrotnie też bezpiecznie: stara karta dostaje undefined, a setQueryData
      // z undefined w TanStack v5 nic nie robi.
      taskNotifs?: NotificationListResult | TaskNotification[];
      pricingNotifs?: MyPricingNotification[];
    }
  | {
      /**
       * Dowolna karta prosi lidera o natychmiastowy fetch (np. po zmutowaniu
       * powiadomienia jako przeczytanego — żeby inne karty też to widziały).
       */
      type: "refresh-please";
    };

// ── Moduł-level singleton do wywoływania odświeżenia z poza hooka ─────────────

/**
 * Referencja do funkcji odświeżającej aktywnej instancji hooka.
 * Singleton na poziomie modułu, bo hooki mutacji (useMarkRead itp.)
 * muszą móc wywołać odświeżenie bez bezpośredniego dostępu do ref-ów hooka.
 *
 * Ustawiana przez hook przy montowaniu, zerowana przy odmontowaniu.
 * Jeśli hook nie jest zamontowany — no-op (graceful degradation).
 */
let _triggerRefresh: (() => void) | null = null;

/**
 * Wywołaj odświeżenie powiadomień ze źródła (lider robi fetch, pozostałe karty
 * dostaną broadcast). Wywoływane z onSuccess mutacji mark-read itp.
 *
 * Bezpieczne do wywołania zawsze — jeśli hook nie jest aktywny, nic się nie dzieje.
 */
export function broadcastRefreshRequest(): void {
  _triggerRefresh?.();
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Montuj ten hook dokładnie raz, wysoko w drzewie komponentów (NotificationBell
 * jest w layoucie, więc montuje się przy każdym zalogowaniu — idealne miejsce).
 *
 * @param isInternal - czy zalogowany user jest pracownikiem wewnętrznym;
 *   jeśli nie, pomijamy endpoint pricing-notifications (contractor nie ma dostępu).
 */
export function useNotificationSync(isInternal: boolean): void {
  const qc = useQueryClient();

  // Czy ta karta jest aktualnym liderem?
  const isLeader = useRef(false);

  // Referencja do aktywnego BroadcastChannel (null = BroadcastChannel niedostępny).
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Timery — trzymamy ref-y, żeby móc je wyczyścić przy cleanup.
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Używamy ref na `isInternal` żeby nie re-tworzyć closures przy każdej zmianie.
  const isInternalRef = useRef(isInternal);
  useEffect(() => { isInternalRef.current = isInternal; }, [isInternal]);

  // Idle detection — śledzi ostatnią aktywność użytkownika.
  const lastActiveAt = useRef(Date.now());
  const wasIdle = useRef(false);

  useEffect(() => {
    // ── Lokalne funkcje (muszą być wewnątrz useEffect, żeby mieć dostęp do refs) ──

    /**
     * Wpisuje świeże dane do TanStack Query cache dla WSZYSTKICH kluczy,
     * z których korzysta NotificationBell i inne komponenty.
     * Korzysta z setQueryData zamiast invalidateQueries — nie robi HTTP.
     */
    function normalizeTaskNotifs(
      taskNotifs: NotificationListResult | TaskNotification[]
    ): NotificationListResult {
      return Array.isArray(taskNotifs) ? { items: taskNotifs, olderCount: 0 } : taskNotifs;
    }

    function applyToCache(message: Extract<SyncMessage, { type: "data" }>): void {
      // Klucze muszą dokładnie odpowiadać tym w useNotifications.ts i useTenders.ts
      const countKey = ["task-notifications", "unread-count"];
      const previous = qc.getQueryData<UnreadCount>(countKey);
      const { unreadCount } = message;
      qc.setQueryData(countKey, unreadCount);
      if (message.taskNotifs) {
        qc.setQueryData(["task-notifications", "list"], normalizeTaskNotifs(message.taskNotifs));
      }
      if (message.pricingNotifs && isInternalRef.current) {
        // pricing-notifications używa [key, unread?] — bez filtra to [key, undefined]
        qc.setQueryData(["pricing-notifications", undefined], message.pricingNotifs);
      }
      // Licznik się ruszył → listy są nieaktualne. invalidateQueries przeładowuje
      // tylko zapytania z aktywnym obserwatorem, czyli otwarty dzwonek (i stronę
      // wycen); zamknięty dzwonek dostaje świeże listy przy otwarciu.
      if (
        previous &&
        (previous.count !== unreadCount.count ||
          previous.pricing_unread !== unreadCount.pricing_unread)
      ) {
        void qc.invalidateQueries({ queryKey: ["task-notifications", "list"], exact: true });
        void qc.invalidateQueries({ queryKey: ["pricing-notifications"] });
      }
    }

    /**
     * Pobiera liczniki i broadcastuje je do innych kart. Wywołuje tylko lider.
     */
    async function fetchAndBroadcast(): Promise<void> {
      try {
        const unreadCount = await notificationsApi.unreadCount();
        const message = { type: "data", unreadCount } satisfies SyncMessage;

        // Aktualizuj własny cache
        applyToCache(message);

        // Wyślij dane do wszystkich pozostałych kart w tej przeglądarce
        channelRef.current?.postMessage(message);
      } catch {
        // Błąd sieciowy — zostaw stary cache, spróbuj ponownie za POLL_MS.
        // Nie rzucamy dalej — hook nie powinien crashować aplikacji.
      }
    }

    /**
     * Ogłoszenie się liderem:
     * 1. Zapisz heartbeat w localStorage (żeby inne karty wiedziały, że żyjesz)
     * 2. Zrób natychmiastowy fetch (nowi liderzy nie czekają do pierwszego ticka)
     * 3. Ustaw cykliczny polling
     * 4. Ustaw cykliczne odświeżanie heartbeatu
     */
    function becomeLeader(): void {
      isLeader.current = true;
      localStorage.setItem(LEADER_STORAGE_KEY, String(Date.now()));

      // No immediate fetchAndBroadcast() here — TanStack Query's useNotifications()
      // and useUnreadCount() already fire HTTP requests on mount. Calling fetchAndBroadcast()
      // immediately would duplicate those requests. The interval below picks up after 30s.
      pollTimerRef.current = setInterval(() => {
        if (Date.now() - lastActiveAt.current > IDLE_THRESHOLD_MS) {
          wasIdle.current = true; // zapamiętaj żeby po powrocie fetch był natychmiastowy
          return;
        }
        void fetchAndBroadcast();
      }, POLL_MS);

      // Heartbeat musi być częstszy niż LEADER_TTL_MS, żeby inne karty nie
      // uznały nas za martwych i nie uruchomiły własnego pollingu.
      heartbeatTimerRef.current = setInterval(() => {
        localStorage.setItem(LEADER_STORAGE_KEY, String(Date.now()));
      }, HEARTBEAT_MS);
    }

    /**
     * Rezygnacja z roli lidera: wyczyść timery, usuń heartbeat.
     * Wywoływane przy odmontowaniu hooka i przy zamknięciu karty (beforeunload).
     */
    function resignLeader(): void {
      if (!isLeader.current) return;
      isLeader.current = false;
      // Do NOT remove the key from localStorage — let it expire naturally (LEADER_TTL_MS).
      // Removing it here would cause React StrictMode's simulated unmount→remount cycle
      // to see "no leader" and claim leadership again, firing a duplicate fetch batch.
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null; }
    }

    /**
     * Sprawdź czy jest żywy lider — jeśli nie, przejmij rolę.
     * Wywoływane przy starcie i co LEADER_TTL_MS przez watchdog.
     *
     * RACE CONDITION i jak ją mitygujemy:
     *
     *   Problem: gdy lider pada, wszystkie karty mogą jednocześnie odczytać
     *   stary timestamp z localStorage i wszystkie wywołać becomeLeader().
     *   localStorage.setItem nie jest atomiczny między kartami — nie ma CAS
     *   (compare-and-swap) dostępnego w przeglądarce bez serwera.
     *
     *   Fix — jitter + double-check:
     *     1. Po stwierdzeniu "lider nie żyje" czekaj LOSOWY czas (0–300 ms).
     *     2. Po odczekaniu sprawdź PONOWNIE — jeśli inna karta już się wpisała
     *        jako lider w tym oknie, odpuść.
     *     3. Tylko jedna karta (ta z najkrótszym jitterem) wygrywa w 99%+ przypadków.
     *
     *   Pozostałe ryzyko: dwie karty mogą wylosować identyczny (lub niemal identyczny)
     *   jitter i obie przejść double-check przed wzajemnym zapisem.
     *   Konsekwencja: przez jeden cykl 30s mamy 2 liderów → 2 requesty zamiast 1.
     *   Nie jest to błąd danych, tylko chwilowy nadmiar requestów.
     *   Prawdziwy atomic leader election wymagałby operacji serwera (np. Redis SET NX).
     */
    async function tryClaimLeader(): Promise<void> {
      if (isLeader.current) return; // już jesteśmy liderem, nic do roboty

      // Pierwszy odczyt: czy w ogóle warto startować wyścigu?
      const ts = Number(localStorage.getItem(LEADER_STORAGE_KEY) ?? 0);
      if (Date.now() - ts < LEADER_TTL_MS) return; // lider żyje, odpuść

      // Jitter: każda karta czeka inny losowy czas (0–300ms).
      // Po tym czasie tylko karta z najkrótszym jitterem prawdopodobnie
      // już wpisała się do localStorage — pozostałe zobaczą świeży timestamp.
      const jitter = Math.random() * 300;
      await new Promise<void>((resolve) => setTimeout(resolve, jitter));

      // Double-check po jitterze: czy ktoś inny zdążył zostać liderem?
      if (isLeader.current) return; // my sami zostaliśmy w międzyczasie (np. z innego miejsca)
      const ts2 = Number(localStorage.getItem(LEADER_STORAGE_KEY) ?? 0);
      if (Date.now() - ts2 < LEADER_TTL_MS) return; // inna karta wygrała wyścig

      // Nikt nie wygrał w naszym oknie — ogłaszamy się liderem.
      becomeLeader();
    }

    /**
     * Wywoływane przy każdym zdarzeniu aktywności użytkownika.
     * Jeśli użytkownik wraca po idle (wasIdle=true), triggeruje natychmiastowy fetch
     * zamiast czekać do następnego cyklu pollingu.
     */
    function markActive(): void {
      const wasPreviouslyIdle = wasIdle.current;
      lastActiveAt.current = Date.now();
      if (!wasPreviouslyIdle) return;
      wasIdle.current = false;
      // Powrót z idle — fetch od razu (nie czekaj do następnego tiku 30s).
      if (isLeader.current) {
        void fetchAndBroadcast();
      } else {
        channelRef.current?.postMessage({ type: "refresh-please" } satisfies SyncMessage);
      }
    }

    const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    const onVisibility = () => { if (document.visibilityState === "visible") markActive(); };
    document.addEventListener("visibilitychange", onVisibility);

    // ── Obsługa braku BroadcastChannel (fallback) ─────────────────────────────

    if (!("BroadcastChannel" in window)) {
      // Stary Safari / niektóre tryby prywatne — każda karta polluje samodzielnie.
      // Zachowanie identyczne jak przed wprowadzeniem tego hooka.
      fetchAndBroadcast();
      pollTimerRef.current = setInterval(() => {
        if (Date.now() - lastActiveAt.current > IDLE_THRESHOLD_MS) {
          wasIdle.current = true;
          return;
        }
        void fetchAndBroadcast();
      }, POLL_MS);
      _triggerRefresh = fetchAndBroadcast;
      return () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        _triggerRefresh = null;
        ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
        document.removeEventListener("visibilitychange", onVisibility);
      };
    }

    // ── Normalny flow z BroadcastChannel ─────────────────────────────────────

    const bc = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = bc;

    bc.onmessage = (event: MessageEvent<SyncMessage>) => {
      if (event.data.type === "data") {
        // Dostaliśmy świeże dane od lidera — wpisz do cache, żaden HTTP request.
        applyToCache(event.data);
      } else if (event.data.type === "refresh-please" && isLeader.current) {
        // Inna karta (np. po oznaczeniu jako przeczytane) prosi nas o natychmiastowy fetch.
        fetchAndBroadcast();
      }
    };

    // Eksponuj funkcję refresh na poziomie modułu — hooki mutacji wywołają ją
    // po onSuccess, żeby inne karty nie czekały do następnego cyklu 30s.
    _triggerRefresh = () => {
      if (isLeader.current) {
        fetchAndBroadcast();
      } else {
        // Nie jesteśmy liderem — poproś lidera przez broadcast.
        bc.postMessage({ type: "refresh-please" } satisfies SyncMessage);
      }
    };

    // Próba zostania liderem — powiedzie się tylko jeśli brak żywego lidera.
    void tryClaimLeader();

    // Watchdog: co LEADER_TTL_MS sprawdź czy lider wciąż żyje.
    // Obsługuje crash lidera, który nie odpalił beforeunload (np. kill procesu, OOM).
    // tryClaimLeader jest async — setInterval ignoruje zwrócony Promise (fire-and-forget).
    // To celowe: kolejny tick nie czeka na zakończenie poprzedniego, co jest tu OK,
    // bo każde wywołanie jest idempotentne i krótkie (jitter max 300ms < LEADER_TTL_MS).
    const watchdogTimer = setInterval(() => { void tryClaimLeader(); }, LEADER_TTL_MS);

    // Czyste zwolnienie roli lidera gdy karta jest zamykana normalnie.
    // Uwaga: beforeunload NIE gwarantuje wywołania (np. mobile, force-close).
    // Dlatego mamy też TTL + watchdog jako backup.
    window.addEventListener("beforeunload", resignLeader);

    return () => {
      resignLeader();
      clearInterval(watchdogTimer);
      bc.close();
      channelRef.current = null;
      _triggerRefresh = null;
      window.removeEventListener("beforeunload", resignLeader);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []); // puste deps — setup raz przy montowaniu, cleanup przy odmontowaniu
}
